/**
 * Clés métier identifiant les fonctionnalités d'un plan d'abonnement.
 * Ces clés sont stockées dans `settings.subscription_config.plans[].tiers[].benefits[]`.
 * L'adaptateur `resolvePlanFeature` les transforme en labels affichables.
 */
export type PlanFeatureKey =
  // Fonctionnalités métier Dendromap
  | 'CREATE_ARBRE'
  | 'CREATE_ALIGNMENT'
  | 'CREATE_EB'
  | 'FICHE_ARBRE'
  | 'FICHE_INTERVENTION'
  | 'DUPLICATE'
  | 'CREATE_PLANTATION'
  | 'CALCUL_BEVA'
  | 'ALERTE_TRAVAUX'
  // Accès et infrastructure
  | 'MOBILE_APP'
  | 'BACK_OFFICE'
  | 'GPS_GEOLOCATION'
  | 'CLOUD_BACKUP'
  // Profil et paramètres
  | 'UPDATE_PROFILE'
  // Exports
  | 'EXPORT_PDF'
  // Support
  | 'SUPPORT_EMAIL'
  | 'SUPPORT_PRIORITY'
  | 'SUPPORT_PHONE'
  // Analytics et collaboration
  | 'ADVANCED_ANALYTICS'
  | 'CUSTOM_REPORTS'
  | 'TEAM_COLLABORATION'
  | 'MULTI_PROJECT'
  | 'API_INTEGRATIONS';

export interface PlanFeatureLabel {
  /** Texte court affiché dans la liste des avantages */
  label: string;
  /** Description optionnelle (tooltip, détail) */
  description?: string;
}

/** Map complète clé → label. Ajouter ici toute nouvelle clé métier. */
export const PLAN_FEATURE_LABELS: Record<PlanFeatureKey, PlanFeatureLabel> = {
  // Fonctionnalités métier Dendromap
  CREATE_ARBRE: {
    label: 'Créer un arbre',
    description: 'Planter un arbre isolé avec ses caractéristiques.',
  },
  CREATE_ALIGNMENT: {
    label: 'Créer un alignement',
    description: 'Déclarer plusieurs arbres implantés en alignement.',
  },
  CREATE_EB: {
    label: 'Créer un espace boisé',
    description: 'Décrire un espace boisé ou boisé complexé.',
  },
  FICHE_ARBRE: {
    label: 'Fiche Arbre',
    description: 'Impression PDF de la fiche détaillée d\'un arbre.',
  },
  FICHE_INTERVENTION: {
    label: 'Fiche d\'intervention',
    description: 'Impression PDF de la fiche d\'intervention.',
  },
  DUPLICATE: {
    label: 'Dupliquer',
    description: 'Dupliquer un inventaire existant.',
  },
  CREATE_PLANTATION: {
    label: 'Gestion des plantations',
    description: 'Créer et suivre des implantations de plantations.',
  },
  CALCUL_BEVA: {
    label: 'Calcul BEVA',
    description: 'Calcul automatique de la valeur d\'agrément des arbres (méthode BEVA).',
  },
  ALERTE_TRAVAUX: {
    label: 'Alerte suivi des travaux',
    description: 'Notifications et suivi automatisé des interventions planifiées.',
  },
  // Accès et infrastructure
  MOBILE_APP: {
    label: '1 application mobile',
    description: 'Accès via l\'application mobile Android.',
  },
  BACK_OFFICE: {
    label: '1 back office personnalisé',
    description: 'Accès à votre espace de gestion en ligne.',
  },
  GPS_GEOLOCATION: {
    label: 'Géolocalisation GPS précise',
    description: 'Positionnement GPS haute précision sur carte.',
  },
  CLOUD_BACKUP: {
    label: 'Sauvegarde cloud',
    description: 'Synchronisation et sauvegarde automatique des données.',
  },
  // Profil et paramètres
  UPDATE_PROFILE: {
    label: 'Modification du profil',
    description: 'Mettre à jour les informations du compte.',
  },
  // Exports
  EXPORT_PDF: {
    label: 'Export PDF',
    description: 'Exporter des rapports au format PDF.',
  },
  // Support
  SUPPORT_EMAIL: {
    label: 'Support par email',
    description: 'Assistance par email.',
  },
  SUPPORT_PRIORITY: {
    label: 'Support prioritaire',
    description: 'Accès prioritaire à l\'équipe support.',
  },
  SUPPORT_PHONE: {
    label: 'Support téléphonique',
    description: 'Assistance par téléphone.',
  },
  // Analytics et collaboration
  ADVANCED_ANALYTICS: {
    label: 'Analyses avancées',
    description: 'Statistiques et tableaux de bord détaillés.',
  },
  CUSTOM_REPORTS: {
    label: 'Rapports personnalisés',
    description: 'Créer des rapports sur mesure.',
  },
  TEAM_COLLABORATION: {
    label: 'Collaboration en équipe',
    description: 'Partager l\'accès avec plusieurs utilisateurs.',
  },
  MULTI_PROJECT: {
    label: 'Gestion multi-projets',
    description: 'Gérer plusieurs projets simultanément.',
  },
  API_INTEGRATIONS: {
    label: 'Intégrations API',
    description: 'Connecter Dendromap à des outils tiers via API.',
  },
};

/**
 * Résout une clé de fonctionnalité en label affichable.
 * Si la clé est inconnue, retourne le texte brut comme fallback
 * pour garantir la rétrocompatibilité avec d'anciennes valeurs textuelles.
 */
export function resolvePlanFeature(key: string): PlanFeatureLabel {
  return PLAN_FEATURE_LABELS[key as PlanFeatureKey] ?? { label: key };
}
